import React, { useState } from "react";
import { Plus, Trash2, Calculator, UploadCloud, Check } from "lucide-react";
import { Modal } from "../../components/common/Modal";
import { useApp } from "../../app/context/AppContext";
import { useNotification } from "../../app/context/NotificationContext";
import { formatCurrency } from "../../utils/formatters";

export function PurchaseOrderModal({ isOpen, onClose }) {
  const { suppliers, products, warehouses, addPurchaseOrder } = useApp();
  const { addToast } = useNotification();

  const [supplierId, setSupplierId] = useState(suppliers[0]?.id || "");
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || "");
  const [expectedDate, setExpectedDate] = useState("2026-08-15");
  const [freightCharges, setFreightCharges] = useState(1500);
  const [packagingCharges, setPackagingCharges] = useState(500);
  const [handlingCharges, setHandlingCharges] = useState(400);
  const [notes, setNotes] = useState("");
  const [invoiceFileName, setInvoiceFileName] = useState("");

  const [items, setItems] = useState([
    {
      productId: products[0]?.id || "",
      productName: products[0]?.name || "",
      orderedQty: 100,
      unitPrice: products[0]?.purchasePrice || 360,
      mrp: products[0]?.MRP || 480,
      total: (products[0]?.purchasePrice || 360) * 100
    }
  ]);

  const handleProductChange = (index, prodId) => {
    const prod = products.find((p) => p.id === prodId);
    if (!prod) return;
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      productId: prod.id,
      productName: prod.name,
      unitPrice: prod.purchasePrice,
      mrp: prod.MRP,
      total: prod.purchasePrice * (newItems[index].orderedQty || 1)
    };
    setItems(newItems);
  };

  const handleQtyChange = (index, qty) => {
    const newItems = [...items];
    const num = Math.max(1, Number(qty) || 1);
    newItems[index].orderedQty = num;
    newItems[index].total = newItems[index].unitPrice * num;
    setItems(newItems);
  };

  const handlePriceChange = (index, price) => {
    const newItems = [...items];
    const num = Math.max(0, Number(price) || 0);
    newItems[index].unitPrice = num;
    newItems[index].total = num * newItems[index].orderedQty;
    setItems(newItems);
  };

  const addItemRow = () => {
    const defaultProd = products[0];
    setItems((prev) => [
      ...prev,
      {
        productId: defaultProd?.id || "",
        productName: defaultProd?.name || "",
        orderedQty: 50,
        unitPrice: defaultProd?.purchasePrice || 100,
        mrp: defaultProd?.MRP || 150,
        total: (defaultProd?.purchasePrice || 100) * 50
      }
    ]);
  };

  const removeItemRow = (index) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const subtotal = items.reduce((sum, i) => sum + (Number(i.total) || 0), 0);
  const totalLandedCost = subtotal + Number(freightCharges) + Number(packagingCharges) + Number(handlingCharges);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedSupplier = suppliers.find((s) => s.id === supplierId);
    const selectedWH = warehouses.find((w) => w.id === warehouseId);

    try {
      await addPurchaseOrder({
        supplierId,
        supplierName: selectedSupplier?.name || "Supplier",
        warehouseId,
        warehouseName: selectedWH?.name || "Warehouse",
        expectedDate,
        freightCharges: Number(freightCharges),
        packagingCharges: Number(packagingCharges),
        handlingCharges: Number(handlingCharges),
        notes,
        invoiceFileName,
        items
      });
      addToast(`Purchase Order created successfully for ${selectedSupplier?.name}!`, "success");
      onClose();
    } catch (err) {
      addToast("Failed to create purchase order.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Multi-Line Purchase Order (PO)"
      subtitle="Issue bulk stock PO with landed cost computation & supplier terms"
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5 text-xs">
        {/* Supplier & Warehouse Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Select Supplier / Distributor *
            </label>
            <select
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.paymentTerms})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Destination Warehouse *
            </label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Expected Delivery Date *
            </label>
            <input
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>
        </div>

        {/* Multi-Line Items Grid */}
        <div className="border border-slate-200 dark:border-slate-800 rounded-2xl p-4 bg-slate-50/50 dark:bg-slate-950/40 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
              <Calculator className="w-4 h-4 text-emerald-500" />
              PO Line Items ({items.length})
            </h4>
            <button
              type="button"
              onClick={addItemRow}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Product Line
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200 dark:border-slate-800">
                  <th className="py-2 px-2">Product</th>
                  <th className="py-2 px-2 w-28">Order Qty</th>
                  <th className="py-2 px-2 w-28">Cost / Unit (₹)</th>
                  <th className="py-2 px-2 w-24">MRP (₹)</th>
                  <th className="py-2 px-2 text-right">Line Total</th>
                  <th className="py-2 px-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/60 dark:divide-slate-800">
                {items.map((row, idx) => (
                  <tr key={idx}>
                    <td className="py-2 px-2">
                      <select
                        value={row.productId}
                        onChange={(e) => handleProductChange(idx, e.target.value)}
                        className="w-full px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
                      >
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} ({p.unit})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="1"
                        value={row.orderedQty}
                        onChange={(e) => handleQtyChange(idx, e.target.value)}
                        className="w-full px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        min="0"
                        value={row.unitPrice}
                        onChange={(e) => handlePriceChange(idx, e.target.value)}
                        className="w-full px-2 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
                      />
                    </td>
                    <td className="py-2 px-2 font-mono text-slate-500">
                      ₹{row.mrp}
                    </td>
                    <td className="py-2 px-2 text-right font-mono font-semibold text-slate-900 dark:text-white">
                      {formatCurrency(row.total)}
                    </td>
                    <td className="py-2 px-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeItemRow(idx)}
                        disabled={items.length <= 1}
                        className="text-slate-400 hover:text-rose-500 disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Landed Cost Breakdown */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-slate-100 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-200/60 dark:border-slate-800">
          <div>
            <label className="block text-slate-500 font-medium mb-1">Freight Charges (₹)</label>
            <input
              type="number"
              value={freightCharges}
              onChange={(e) => setFreightCharges(e.target.value)}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-slate-500 font-medium mb-1">Packaging / Strapping (₹)</label>
            <input
              type="number"
              value={packagingCharges}
              onChange={(e) => setPackagingCharges(e.target.value)}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-slate-500 font-medium mb-1">Labor / Unloading (₹)</label>
            <input
              type="number"
              value={handlingCharges}
              onChange={(e) => setHandlingCharges(e.target.value)}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg"
            />
          </div>
          <div className="text-right flex flex-col justify-end">
            <p className="text-slate-500">Total Landed Cost</p>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 font-display">
              {formatCurrency(totalLandedCost)}
            </p>
          </div>
        </div>

        {/* Notes & Mock Invoice Attachment */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Logistics / Packaging Instructions
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Ensure dry tarpaulin covering, palletized delivery..."
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Proforma / Invoice Attachment (Mock Upload)
            </label>
            <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-3 flex items-center justify-center gap-2 text-slate-500 bg-white dark:bg-slate-900">
              <UploadCloud className="w-5 h-5 text-slate-400" />
              <input
                type="file"
                onChange={(e) => setInvoiceFileName(e.target.files[0]?.name || "Invoice.pdf")}
                className="text-[11px] text-slate-400 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 dark:file:bg-slate-800 dark:file:text-slate-300"
              />
            </div>
          </div>
        </div>

        {/* Submit Controls */}
        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 rounded-xl font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            Confirm & Transmit PO
          </button>
        </div>
      </form>
    </Modal>
  );
}
